import { RefreshOutlined } from '@mui/icons-material';
import { Alert, Box, Grid } from '@mui/material';
import { SahinButton } from '@sahinyazilim/sahinyazilim-components';
import { SahinDataTable } from 'common/components/DataTable/SahinDataTable';
import { EmptyState } from 'common/components/EmptyState';
import { FailedLoad } from 'common/components/FailedLoad/FailedLoad';
import { Loading } from 'common/components/Loading/Loading';
import { useAppDispatch, useAppSelector } from 'common/context/hooks';
import { useFetch } from 'common/hooks/useFetchSwr';
import { AdminTablePaths } from 'common/model/AdminTablePaths';
import { Nullable } from 'common/model/Nullable';
import { orderActions } from 'context/OrderSlice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { OrderForm } from '../Form/OrderForm';
import { orderListColumns } from './OrderList.types';
import { AutoStatusToolbar } from './components/AutoStatusToolbar';
import { NotificationManager } from './components/NotificationManager';
import { OrderStatusControl } from './components/OrderStatusControl';
import { StatusFilterTabs } from './components/StatusFilterTabs';

export const COMPONENT_TABLE_PATH = AdminTablePaths.ORDER;

export const OrderList = () => {
  const dispatch = useAppDispatch();
  const { newOrderCount } = useAppSelector((state) => state.order);
  const { branchId } = useParams<{ branchId: string }>();

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedItem, setSelected] = useState<Nullable<any>>(null);

  const getApiUrl = useCallback(() => {
    return `${COMPONENT_TABLE_PATH}/status/${statusFilter}?page=${currentPage}&per_page=${perPage}&branch=${branchId}`;
  }, [currentPage, perPage, statusFilter, branchId]);

  const getStatusCountsUrl = useCallback(() => {
    return `${COMPONENT_TABLE_PATH}/status-counts?branch=${branchId}`;
  }, [branchId]);

  const { data, error, isLoading, totalData, totalPage, mutate } = useFetch<
    Order[]
  >(getApiUrl() || '', {
    revalidateOnFocus: false,
  });

  const { data: statusCountsData } = useFetch<Record<string, number>>(
    getStatusCountsUrl() || '',
    {
      revalidateOnFocus: false,
    },
  );

  const onEdit = (selected: any) => {
    setSelected(selected);
  };

  const onCloseModal = () => {
    setSelected(null);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPerPage(newPageSize);
    setCurrentPage(1);
  };

  const orderStatusControl = (params: any) => {
    return <OrderStatusControl params={params} onMutate={mutate} />;
  };

  const handleStatusFilterChange = (newValue: string) => {
    setStatusFilter(newValue);
  };

  const handleRefresh = useCallback(() => {
    setStatusFilter('pending');
    dispatch(orderActions.setNewOrderCount(0));
    mutate();
  }, [dispatch, mutate]);

  useEffect(() => {
    if (data?.[0]?.id && statusFilter === 'pending') {
      dispatch(orderActions.setLastOrderId(Number(data[0].id)));
    }
  }, [data, dispatch, statusFilter]);

  useEffect(() => {
    if (newOrderCount > 0 && statusFilter === 'pending') {
      handleRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOrderCount]);

  // Filtreleme değişikliklerinde sayfa sıfırla
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const customToolbar = useMemo(
    () => (
      <Grid item xs={12} container>
        {newOrderCount > 0 && (
          <Grid xs={12} container>
            <Alert
              severity="warning"
              sx={{ width: '100%', borderRadius: 0 }}
              action={
                <SahinButton
                  startIcon={<RefreshOutlined />}
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleRefresh}
                >
                  HEMEN YENİLE
                </SahinButton>
              }
            >
              <b>{newOrderCount} adet</b> yeni sipariş var! görüntülemek için
              siparişleri yenileyiniz
            </Alert>
          </Grid>
        )}
        <StatusFilterTabs
          statusFilter={statusFilter}
          statusCounts={statusCountsData}
          onStatusFilterChange={handleStatusFilterChange}
        />
      </Grid>
    ),
    [newOrderCount, handleRefresh, statusFilter, statusCountsData],
  );

  const rightToolbox = useMemo(
    () => (
      <Grid gap={2} wrap="nowrap" container>
        <AutoStatusToolbar />
        <NotificationManager />
      </Grid>
    ),
    [],
  );

  if (error) return <FailedLoad />;
  return (
    <>
      <OrderForm
        open={Boolean(selectedItem)}
        initialData={selectedItem}
        handleClose={onCloseModal}
      />

      {customToolbar}

      {isLoading ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            borderRadius: 1,
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Loading />
        </Box>
      ) : (
        <SahinDataTable
          data={data}
          columns={orderListColumns}
          onEdit={onEdit}
          customActionComponent={orderStatusControl}
          actionButtonColumnWidth={350}
          currentPage={currentPage}
          totalPages={totalPage || 1}
          totalData={totalData || 0}
          onPageChange={handlePageChange}
          pageSize={perPage}
          onPageSizeChange={handlePageSizeChange}
          rightToolbox={rightToolbox}
          customEmptyMessage={
            <EmptyState
              title="Henüz Sipariş Yok"
              variant="orders"
              icon="📋"
              iconSize={55}
            />
          }
        />
      )}
    </>
  );
};
